import { numberToDollars, roundPercent, percentParts, percentLabel } from '../../../../utils/utils'
import { Accent } from '../../../../components/AdviceSummary'
import { RangeScale } from '../../../../components'
import { RiskValue } from './style'

export const dollars = value => `$${numberToDollars(value ?? 0, true)}`

// In desktop modified values, symbols and units use the primary color while
// digits remain dark. Current and mobile values use plain text.
const accentedDollars = value => (
  <>
    <Accent>$</Accent>
    {numberToDollars(value ?? 0, true)}
  </>
)

const accentDollarLabel = label => (
  <>
    <Accent>{label.charAt(0)}</Accent>
    {label.slice(1)}
  </>
)

const pct = ({ prefix = '', value }) => (
  <>
    {prefix && <Accent>{prefix}</Accent>}
    {value}
    <Accent>%</Accent>
  </>
)

const accentPair = (secondary, money) => (
  <>
    {secondary}
    <Accent> | </Accent>
    {money}
  </>
)

export const createSummaryFormatters = (isCompact, theme) => {
  // Savings are shown monthly here to match the contributions step.
  const monthUnit = isCompact ? '/mo' : '/month'

  // Desktop renders the secondary metric inline; mobile stacks it below the amount.
  const metricPair = (dollarFigure, secondary) => {
    if (secondary == null) {
      return dollarFigure
    }

    return isCompact ? (
      <>
        <span>{dollarFigure}</span>
        <span>{`(${secondary})`}</span>
      </>
    ) : (
      `${secondary} | ${dollarFigure}`
    )
  }

  // Include the percentage only when savings are defined as a percent of pay.
  const savingsValue = ({ monthly, percent, isPercentOfPay }, accent = false) => {
    const rounded = roundPercent(percent)

    if (!accent || isCompact) {
      return metricPair(`${dollars(monthly)}${monthUnit}`, isPercentOfPay ? `${rounded}%` : null)
    }

    const money = (
      <>
        {accentedDollars(monthly)}
        <Accent>{monthUnit}</Accent>
      </>
    )

    return isPercentOfPay ? accentPair(pct({ value: rounded }), money) : money
  }

  const additionalValue = (monthly, accent = false) =>
    !accent || isCompact ? (
      `${dollars(monthly)}${monthUnit}`
    ) : (
      <>
        {accentedDollars(monthly)}
        <Accent>{monthUnit}</Accent>
      </>
    )

  const goalsValue = ({ count, total }, accent = false) => {
    const countText = `${count} ${count === 1 ? 'goal' : 'goals'}`

    return !accent || isCompact
      ? metricPair(dollars(total), countText)
      : accentPair(countText, accentedDollars(total))
  }

  const companyStockValue = ({ percent, dollar }, accent = false) =>
    !accent || isCompact
      ? metricPair(dollar, `${percentLabel(percent)}%`)
      : accentPair(pct(percentParts(percent)), accentDollarLabel(dollar))

  // Show the range indicator only on desktop. Hide it when the value is outside
  // the preferred range because the label already shows whether it is lower or higher.
  const riskValue = ({ text, index }, accent = false) => {
    const label = accent && !isCompact ? <Accent>{text}</Accent> : text

    return index >= 0 && !isCompact ? (
      <RiskValue>
        {label}
        <RangeScale
          rectangleActive={index}
          theme={theme}
          variant={accent ? 'recommended' : 'current'}
        />
      </RiskValue>
    ) : (
      label
    )
  }

  return { savingsValue, additionalValue, goalsValue, companyStockValue, riskValue }
}
